import { Box, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  ACCRUAL_BALANCE_PAGE,
  LAST_PUNCH_PAGE,
  ONLINE_SCHEDULE_SELF_SERVICE,
  REACT_ATTESTATION,
  REACT_EMPLOYEE_LOGIN_SCREEN,
  REACT_GENERAL_QUESTION,
  REACT_LABOR_CODE,
  REACT_PUNCH_LOCKOUT,
  RESET_PASSWORD,
} from "../../constants/ReactEndPoints";
import { getEmployeeShiftData, getFunctionKeysList } from "../../services/FunctionKeysService";
import CommonUtil from "../../util/CommonUtils";
import {
  getDateTimeInMs,
  getDurationText,
  getFunctionKeyDetails,
  getLastPunchData,
  isDuplicatePunch,
  isRuleEnabled,
  isUserEarlyClockIn_ClockOut,
  isUserLateClockIn,
  isUserLateClockOut,
  isUserUnderShift,
  getSourceMeta,
  updateObjectInArray,
} from "../../util/helperFunction";
import { FloatButton, Menu, Popover } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { format, addDays, isBefore } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import useScreenSize from "../../util/customHooks/useScreenSize";
import CustomCarousel from "../../components/CustomCarousel";
import PromptMessageModel from "../../components/PromptMessageModel";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCurrentFunctionKey,
  storeEmployeeShiftData,
  storeShiftId,
  resetShiftId,
  updateCurrentFunctionKey,
  updateProvision,
} from "../../feature/Dashboard/dashboardSlice";
import AppConstant from "../../util/AppConstant";
import { selectProvision } from "../../feature/Dashboard/dashboardSelectors";
import { useTranslation } from "react-i18next";

export default function Dashboard() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [punchMenusDisplay, setPunchMenusDisplay] = useState([]);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptMessage, setPromptMessage] = useState("");
  const [visible, setVisible] = useState(false);
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const {
    attendanceRule = {},
    punchMenus = [],
    timeSource = "system",
    ignore_shift_lockout_outside_shift_time = "false",
    check_consecutive_punch_for = "0",
  } = useSelector(selectProvision);

  const {
    mealBreakLockoutPeriod = 0, //in minutes
    enableMealLockout = false,
    enableMealAttestation = false,              
    enableSeparateAttestationForLockout = false,
    enableGeneralAttestation = false,
    consecutivePunchLockoutPeriod = 0, //in seconds
    acceptConsecutiveLockoutPunch = false,
    shiftDataUnavailablePunchAction = "acceptPunch",
    enableClockLevelShiftScheduleLockout = false,
    checkShiftLateStartEnd = false,
    enableShiftLockoutAttestation = false,
    shiftEarlyStartAttProfile = 0,
    shiftEarlyEndAttProfile = 0,
    shiftLateEndAttProfile = 0,
    shiftLateStartAttProfile = 0,
    logScheduleLockedoutPunchData = false,
    logMealBreakLockedoutPunchData = false,
  } = attendanceRule || {};

  const handleButtonClick = () => {
    setVisible(!visible);
  };

  const handleMenuClick = (key) => {
    setVisible(false);
    switch (key) {
      case "updatePassword":
        navigate(RESET_PASSWORD);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!CommonUtil.isTruthyValue(localStorage.getItem("isLoggedIn"))) {
      navigate(REACT_EMPLOYEE_LOGIN_SCREEN, { replace: true });
    }

    dispatch(clearCurrentFunctionKey());

    localStorage.removeItem("customMappingData");

    dispatch(resetShiftId());

    fetchProvisionData();
  }, [dispatch]);

  const fetchProvisionData = () => {
    const alreadyShown = sessionStorage.getItem("prompt_shown");
    getFunctionKeysList()
      .then((res) => {
        if (res?.status !== 200 || !res?.data) return;
        const { webpunch_enable_prompt, webpunch_prompt_message_text } = res?.data;
        dispatch(updateProvision(res.data));
        if (
          !alreadyShown &&
          webpunch_enable_prompt &&
          webpunch_enable_prompt.trim().toLowerCase() == "true" &&
          !CommonUtil.isEmptyString(webpunch_prompt_message_text)
        ) {
          setShowPrompt(true);
          setPromptMessage(webpunch_prompt_message_text);
        }
      })
      .catch((error) => {
        console.error("Error while fetching the provision data:", error);
      });
  };

  const fetchEmployeeShiftData = async () => {
    try {
      const fromDate = format(new Date(), "yyyy-MM-dd");
      const toDate = format(addDays(new Date(), 1), "yyyy-MM-dd");

      const res = await getEmployeeShiftData(fromDate, toDate);
      if (res?.status === 200 && res.data?.message === "Success" && !CommonUtil.isEmpty(res.data.data)) {
        const shiftData = res.data.data;
        dispatch(storeEmployeeShiftData(shiftData));
        return shiftData;
      } else {
        dispatch(storeEmployeeShiftData([]));
      }
    } catch (error) {
      console.error("Error while fetching the employee shift data", error);
    }
    return [];
  };

  const handleSelfServiceFunction = (eventCode) => {
    if (eventCode == "9") {
      navigate(ACCRUAL_BALANCE_PAGE);
    }
    if (eventCode == "15") {
      navigate(LAST_PUNCH_PAGE);
    }
    if (eventCode == "14") {
      navigate(ONLINE_SCHEDULE_SELF_SERVICE);
    }
  };

  const createPunchPayload = (menuObj, eventCode, currentTime) => ({
    customV11: localStorage.getItem("terminal") || "",
    addedBy: "EMPLOYEE",
    addedDate: currentTime,
    attendanceAction: getFunctionKeyDetails(menuObj?.menuIcon)?.action,
    attendanceActionLabel: getFunctionKeyDetails(menuObj?.menuIcon)?.label,
    attendanceKey: uuidv4(),
    employeeNumber: localStorage.getItem("employeeNumber") || "",
    eventCode: eventCode,
    issueType: 0,
    punchDate: currentTime,
    punchDateStr: new Date()
      .toLocaleDateString(undefined, {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
      })
      .toString(),
    punchMode: 1,
    status: "OPEN",
    syncConfirmationTime: 0,
    syncSentTime: 0,
    syncStatus: 0,
    transactionId: Math.floor(Math.random() * 100001),
    verifyType: "3",
    sourceType: `web_${localStorage.getItem("accountType") || ""}`,
    sourceMeta: JSON.stringify(getSourceMeta()),
    shiftId: 0,
  });

  const handleFunctionKeyClick = async (menuObj) => {
    dispatch(updateCurrentFunctionKey(menuObj));

    const dateTimeInMs = timeSource === "api" ? await getDateTimeInMs() : Date.now();
    const punchDateAndTime = format(dateTimeInMs, "yyyy-MM-dd HH:mm");
    let userFallUnderShift = {};
    let shiftData = null;
    let isEmployeeUnderShift = false;
    let isShowShiftLockoutAtt = false;
    let shiftLockoutAtt_profile = 0;

    const splitCondition = CommonUtil.parseIntoNumeric(menuObj?.splitPunchCondition);
    const splitDuration = CommonUtil.parseIntoNumeric(menuObj?.splitPunchTimeDuration);
    const splitPunchTime = CommonUtil.splitPunchDateAndTime(dateTimeInMs, splitCondition, splitDuration);

    let punchPayload =
      menuObj?.addClosePunch == true && !CommonUtil.isEmptyString(menuObj?.closePunchEventCode)
        ? [
            createPunchPayload(menuObj, menuObj?.eventCode, dateTimeInMs),
            createPunchPayload(menuObj, menuObj?.closePunchEventCode, splitPunchTime),
          ]
        : [createPunchPayload(menuObj, menuObj?.eventCode, dateTimeInMs)];

    /**
     * @description description Process rules dynamically in the order they appear in menuObj.rules
     */
    const rules = menuObj?.rules || [];
    for (const ruleName of rules) {
      switch (ruleName) {
        /**
         * @description Shift lockout check
         */
        case AppConstant.SHIFT_LOCKOUT_RULE: {
          const isShiftLockoutEnable = isRuleEnabled(menuObj, AppConstant.SHIFT_LOCKOUT_RULE);

          if (!isShiftLockoutEnable) break;

          const latestEmployeeShiftData = await fetchEmployeeShiftData();

          const isPunchDeniedWithoutShiftData = shiftDataUnavailablePunchAction === "denyPunch";
          const isEnableClockLevelShiftLockout = enableClockLevelShiftScheduleLockout;

          userFallUnderShift = isUserUnderShift(latestEmployeeShiftData, attendanceRule, punchDateAndTime);
          shiftData = userFallUnderShift?.shiftData;
          isEmployeeUnderShift = userFallUnderShift?.isEmployeeUnderShift;
          dispatch(storeShiftId(shiftData?.shiftId || 0));

          if (isEnableClockLevelShiftLockout) {
            const isEnableAllShiftAtt = checkShiftLateStartEnd;
            const isEnableShiftLockoutAtt = enableShiftLockoutAttestation;
            const isShiftAttEnable = isEnableAllShiftAtt && isEnableShiftLockoutAtt && shiftData;

            //Employee have shift and currently employee not fall under the shift(Here checking early clock_in)
            if (
              isShiftAttEnable &&
              !isEmployeeUnderShift &&
              isUserEarlyClockIn_ClockOut(menuObj, punchDateAndTime, shiftData, attendanceRule, "clock_in")
            ) {
              shiftLockoutAtt_profile = shiftEarlyStartAttProfile;
              isShowShiftLockoutAtt = true;
            }
            //Employee have shift and currently employee fall under the shift(Here checking late clock_in)
            else if (
              isEnableAllShiftAtt &&
              isEnableShiftLockoutAtt &&
              shiftData &&
              isEmployeeUnderShift &&
              isUserLateClockIn(menuObj, punchDateAndTime, shiftData, attendanceRule)
            ) {
              shiftLockoutAtt_profile = shiftLateStartAttProfile;
              isShowShiftLockoutAtt = true;
            }
            //Employee have shift and currently employee fall under the shift(Here checking early clock_out)
            else if (
              isShiftAttEnable &&
              isEmployeeUnderShift &&
              isUserEarlyClockIn_ClockOut(menuObj, punchDateAndTime, shiftData, attendanceRule, "clock_out")
            ) {
              shiftLockoutAtt_profile = shiftEarlyEndAttProfile;
              isShowShiftLockoutAtt = true;
            }
            //Employee have shift but currently employee not fall under the shift(Here checking late clock_out)
            else if (
              isShiftAttEnable &&
              !isEmployeeUnderShift &&
              isUserLateClockOut(menuObj, punchDateAndTime, shiftData, attendanceRule)
            ) {
              shiftLockoutAtt_profile = shiftLateEndAttProfile;
              isShowShiftLockoutAtt = true;
            }
          }

          // Shift Lockout: outside schedule handling
          if (
            isEnableClockLevelShiftLockout &&
            isShiftLockoutEnable &&
            !isEmployeeUnderShift &&
            !isShowShiftLockoutAtt &&
            (isPunchDeniedWithoutShiftData || (!isPunchDeniedWithoutShiftData && !CommonUtil.isEmpty(shiftData)))
          ) {
            if (
              CommonUtil.isEmpty(shiftData) ||
              (!CommonUtil.isEmpty(shiftData) && !CommonUtil.isTruthyValue(ignore_shift_lockout_outside_shift_time))
            ) {
              const SHIFT_LOCKOUT_MSG = t("dashboard.shiftLockout.message");
              const LABEL = t("dashboard.shiftLockout.label");
              navigate(REACT_PUNCH_LOCKOUT, {
                state: {
                  message: SHIFT_LOCKOUT_MSG,
                  lockoutType: LABEL,
                  punchPayload,
                  isLoggedPunchData: logScheduleLockedoutPunchData,
                  issueTypeCode: AppConstant.ISSUE_TYPE.SHIFT_DATA_UNAVAILABLE,
                },
              });
              return;
            }
          }

          break;
        }

        /**
         * @description Meal Lock Check
         */
       case AppConstant.MEAL_LOCKOUT_RULE: {
          if (!enableMealLockout || !isRuleEnabled(menuObj, AppConstant.MEAL_LOCKOUT_RULE)) break;

          const lastPunch = await getLastPunchData();
          const { punchDate, eventCode } = lastPunch || {};

          if (!punchDate) break;

          const mealLockOutPeriod = mealBreakLockoutPeriod * 60 * 1000;
          const lastPunchTimeWithMealPeriod = toZonedTime(punchDate + mealLockOutPeriod);
          const formattedTime = format(lastPunchTimeWithMealPeriod, "hh:mm:ss a");
          const MEAL_LOCK_OUT_MSG = t("dashboard.mealLockout.message", { time: formattedTime });
          const LABEL = t("dashboard.mealLockout.label");

          if (!isBefore(new Date(), lastPunchTimeWithMealPeriod)) break;

          const isLastPunchInType = ["0", "3", "4"].includes(String(eventCode || ""));

          if (isLastPunchInType) {
            punchPayload = updateObjectInArray(
              punchPayload,
              {
                issueType: 4,
                status: "PENDING",
                nonCheckFlag: true,
              },
              0
            );
            break;
          } 

          if (!enableMealAttestation) {
            navigate(REACT_PUNCH_LOCKOUT, {
              state: {
                message: MEAL_LOCK_OUT_MSG,
                lockoutType: LABEL,
                punchPayload,
                isLoggedPunchData: false,
                issueTypeCode: AppConstant.ISSUE_TYPE.MEAL_LOCKOUT,
              },
            });
            return;
          }

          const separateProfile = CommonUtil.parseNumberSafe(menuObj?.lockout_attest_profile);
          const menuProfile = CommonUtil.parseNumberSafe(menuObj?.attest_profile);

          if (enableSeparateAttestationForLockout) {
            if (separateProfile > 0) {
              navigate(REACT_ATTESTATION, {
                state: {
                  punchPayload,
                  profileId: separateProfile,
                  isMealLockoutAttestation: true,
                  mealLockoutIssueType: 6,
                },
              });
              return;
            }

            const updatedPunchPayload = updateObjectInArray(
              punchPayload,
              {
                issueType: 6,
                status: "PENDING",
              },
              0
            );

            navigate(REACT_GENERAL_QUESTION, {
              state: {
                punchPayload: updatedPunchPayload,
              },
            });
            return;
          }

          if (menuProfile > 0) {
            navigate(REACT_ATTESTATION, {
              state: {
                punchPayload,
                profileId: menuProfile,
                isMealLockoutAttestation: true,
                mealLockoutIssueType: 6,
              },
            });
            return;
          }

          const updatedPunchPayload = updateObjectInArray(
              punchPayload,
              {
                issueType: 6,
                status: "PENDING",
              },
              0
            );

          navigate(REACT_GENERAL_QUESTION, {
            state: {
              punchPayload: updatedPunchPayload,
            },
          });
          return;
        }

        /**
         * @description Duplicate punch lockout check
         */
        case AppConstant.DUPLICATE_PUNCH_RULE: {
          if (!isRuleEnabled(menuObj, AppConstant.DUPLICATE_PUNCH_RULE)) break;

          const lastPunch = await getLastPunchData();
          if (
            isDuplicatePunch(
              menuObj.eventCode,
              dateTimeInMs,
              consecutivePunchLockoutPeriod,
              check_consecutive_punch_for,
              lastPunch,
            )
          ) {
            const totalSeconds = CommonUtil.parseNumberSafe(consecutivePunchLockoutPeriod);
            const durationText = getDurationText(totalSeconds);

            const DUPLICATE_PUNCH_LOCKOUT_MSG = t("dashboard.duplicatePunchLockout.message",{duration:durationText});
            const LABEL =t("dashboard.duplicatePunchLockout.label");

            navigate(REACT_PUNCH_LOCKOUT, {
              state: {
                message: DUPLICATE_PUNCH_LOCKOUT_MSG,
                lockoutType: LABEL,
                punchPayload,
                isLoggedPunchData: acceptConsecutiveLockoutPunch,
                issueTypeCode: AppConstant.ISSUE_TYPE.INVALID_PUNCH_TIME,
              },
            });
            return;
          }
          break;
        }

        default:
          break;
      }
    }

    const parsedShiftProfile = CommonUtil.parseNumberSafe(shiftLockoutAtt_profile);
    const parsedMenuProfile = CommonUtil.parseNumberSafe(menuObj?.attest_profile);

    const profileId = parsedShiftProfile !== 0 ? parsedShiftProfile : parsedMenuProfile !== 0 ? parsedMenuProfile : 0;

    if ((enableGeneralAttestation || isShowShiftLockoutAtt) && profileId > 0) {
      navigate(REACT_ATTESTATION, {
        state: {
          punchPayload,
          profileId,
        },
      });
    } else if (menuObj?.codeLevels.length > 0) {
      navigate(REACT_LABOR_CODE, {
        state: {
          punchPayload: punchPayload,
        },
      });
    } else {
      navigate(REACT_GENERAL_QUESTION, {
        state: {
          punchPayload: punchPayload,
        },
      });
    }
  };

  const handlePromptCancel = () => {
    sessionStorage.setItem("prompt_shown", "true");
    setShowPrompt(false);
  };

  const menu = (
    <Menu style={{ border: "none" }} onClick={(e) => handleMenuClick(e.key)}>
      <Menu.Divider />
      {/* Update Password */}
      <Menu.Item key="updatePassword">
        <Typography variant="popoverItems">{t("dashboard.settingsMenu.updatePassword")}</Typography>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Grid
        container
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          padding: "0px 20px 100px 20px",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top:
              screenSize.zoomLevel <= 75
                ? "150px"
                : screenSize.isTablet
                  ? "100px"
                  : screenSize.isMobile
                    ? "130px"
                    : "85px",
            display: "flex",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          {window.location.href.includes("/function_key") && localStorage.getItem("isLoggedIn") && (
            <Typography
              sx={{
                fontWeight: "700",
                textAlign: "center",
                width: "100%",
                fontFamily: "inter",
                whiteSpace: "nowrap",
                fontSize: "18px",
                color: "#00A4FF",
              }}
            >
              {t("dashboard.greeting", { name: localStorage.getItem("employeeName") })}
            </Typography>
          )}
        </Box>

        <Grid item container justifyContent={"center"} alignItems={"center"} spacing={3}>
          {punchMenusDisplay?.length > 0 &&
            punchMenusDisplay.map((punchMenuObj, index) => (
              <Grid item key={index} xs={6} sm={3} md={3} lg={3} xl={3}>
                <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
                  <Box
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    component={Paper}
                    elevation={10}
                    backgroundColor={"black"}
                    sx={{
                      borderRadius: "10px",
                      width: "80px",
                      height: "80px",
                    }}
                  >
                    <img
                      src={getFunctionKeyDetails(punchMenuObj?.menuIcon)?.src}
                      alt={`${punchMenuObj.menuIcon}.png`}
                      style={{
                        width: "70px",
                        height: "70px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (AppConstant.SELF_SERVICE_EVENT_CODES.includes(punchMenuObj?.eventCode)) {
                          handleSelfServiceFunction(punchMenuObj?.eventCode);
                        } else {
                          handleFunctionKeyClick(punchMenuObj);
                        }
                      }}
                    />
                  </Box>
                  <Box display={"flex"}>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        textAlign: "center",
                        marginTop: "10px",
                        width: "100%",
                        fontFamily: "inter",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {punchMenuObj?.label}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
        </Grid>

        <Popover
          content={menu}
          trigger="click"
          open={visible}
          onOpenChange={setVisible}
          placement="topRight"
          arrow={{ pointAtCenter: true }}
          sizePopupArrow="50"
        >
          <FloatButton
            onClick={handleButtonClick}
            icon={<SettingOutlined />}
            type="default"
            style={{
              insetInlineEnd: 8,
              bottom: 10,
              position: "fixed",
            }}
          />
        </Popover>

        <CustomCarousel
          screenSize={screenSize}
          punchMenus={punchMenus || []}
          setPunchMenusDisplay={setPunchMenusDisplay}
        />

        {/* Prompt message modal */}
        <PromptMessageModel 
        isModalOpen={showPrompt} 
        message={promptMessage} 
        handlePromptCancel={handlePromptCancel}
        />

      </Grid>
    </>
  );
}
